import NumericMixin from '../../../mixins/numeric-mixin';

import type { ClinicalBaseControlProps } from '../clinical-base-control';
import { ClinicalBaseControl } from '../clinical-base-control';

import { bind } from 'bind-decorator';
import OutlineButton from 'bz-core-controls/controls/general/outline-button';
import _ from 'lodash';
import { observer } from 'mobx-react';
import React from 'react';
import type { ScoresGoalsSettingsResponse } from 'tm3-core/api.dtos';
import { colours } from '../../../styles/colours';
import ButtonGroup from '../../form/button-group';
import NumPad from '../../general/number/num-pad';
import { QuickPickContainer } from '../../general/quick-pick/container';
import Popover from '../../modal/popover';
import ReadOnly from '../read-only';

export interface LinkedNumericValue {
  value: number | string | null;
  scoreEntryId: number | null;
}

type ValueChangeEventHandler = (value: ClinicalNumericValue) => void;
export type ClinicalNumericValue = LinkedNumericValue | number | string;

interface IProps extends ClinicalBaseControlProps {
  value: ClinicalNumericValue;
  scoreGoalLink: ScoresGoalsSettingsResponse;
  decimals: number;
  warning: boolean; // can probably extract these into some common clinical config interface
  errored: boolean;
  readOnly: boolean;
  max: number;
  min: number;
  _max?: number;
  inline: boolean;
  onUpdate: ValueChangeEventHandler;
  onChange: ValueChangeEventHandler;
  onUnitChange: (unit: string) => void;
  placeholder: string;
  disabled: boolean;
  unit?: string;
  valueUnit?: string;
  src?: string[];
  label?: string;
  hideLabel?: boolean;
}

interface IState {
  value: ClinicalNumericValue;
  open: boolean;
  anchorEl: HTMLElement | null;
  unit: string;
  caret: number;
}

class Numeric extends React.Component<IProps, IState> {
  static defaultProps = {
    value: null,
    decimals: 0,
    warning: false,
    errored: false,
    max: Infinity,
    min: -Infinity,
    // eslint-disable-line no-empty, @typescript-eslint/no-empty-function
    onUpdate: () => {},
    // eslint-disable-line no-empty, @typescript-eslint/no-empty-function
    onChange: () => {}
  };

  _refs: { [key: string]: React.RefObject<NumPad> } = {};

  constructor(props: IProps) {
    super(props);
    this._refs = {
      numpad: React.createRef()
    };
    const value = this.parseValFromProps(props);

    this.state = {
      open: false,
      unit: '',
      value,
      anchorEl: null,
      caret: 0
    };
  }

  get limitedMax() {
    const { max, scoreGoalLink } = this.props;
    if (this.fieldIsLinkedToScore()) {
      return scoreGoalLink.type === 'G' ? 100 : scoreGoalLink.max || max;
    }
    return max;
  }

  get limitedMin() {
    const { min, scoreGoalLink } = this.props;
    if (this.fieldIsLinkedToScore()) {
      return scoreGoalLink.type === 'G' ? 0 : scoreGoalLink.min || min;
    }
    return min;
  }

  parseValFromProps(props: IProps) {
    let val: ClinicalNumericValue = props.value;
    if (props.value && typeof props.value === 'string' && props.value.indexOf('{') >= 0) {
      val = JSON.parse(props.value) as LinkedNumericValue;
    }
    return val;
  }
  UNSAFE_componentWillReceiveProps(next: IProps) {
    const value = this.parseValFromProps(next);
    if (this.state.value !== value) {
      this.setState({ value });
    }
  }
  toDecimal(newValue: number, start?: number, changeCallback?: () => void) {
    const isBlank = /^\.|^\-$|\-\.|^$/;
    const isNegative = /^-/;
    const isInteger = /^\-?\d{1,}$/;
    const isDecimal = /^\-?\d*\.\d*$/;
    const toTruncLeft = /^(\-)?0\d{1,}/;
    const truncLeft = /^\-?\b0*([1-9][0-9]*|0)\b/;
    let { caret } = this.state;

    let value: number | string = (
      newValue ||
      this.getNumericValue(this.state.value) ||
      0
    ).toString();

    let dp = this.props.decimals;

    if (isBlank.test(value)) {
      value = value.match(isNegative) ? '-0' + value.substring(1) : '0' + value;
      if (parseFloat(value) === 0.0) {
        caret = start;
      } else if (start >= 0) {
        caret = start;
      }
    }

    if (!/\./.test(value)) {
      value += '.' + NumericMixin.pad(dp);
      if (start >= 0) {
        caret = start;
      }
    }

    if (!isDecimal.test(value)) {
      this.setState({ caret }, changeCallback);
      return;
    }

    if (toTruncLeft.test(value)) {
      let neg = value.match(isNegative);
      let diff = value.length;
      value = (neg ? '-' : '') + value.replace(truncLeft, value.match(truncLeft)![1]);
      diff -= value.length;
      if (start >= 0) {
        caret = start == (neg ? 1 : 0) ? start : start - diff;
      }
    }

    let padRight = new RegExp('\\.\\d{0,' + (parseInt(dp) - 1) + '}$');
    let toTruncRight = new RegExp('\\.\\d{' + (parseInt(dp) + 1) + ',}');

    if (padRight.test(value)) {
      value = value + NumericMixin.pad(dp);
    }

    if (toTruncRight.test(value)) {
      const shift = Math.pow(10, dp);
      value = (Math.round(Number(value) * shift) / shift).toFixed(dp);
      if (start >= 0) {
        caret = start < value.length ? start : value.length;
      }
    }

    value = NumericMixin.limit(value, this.props.min, this.props.max);
    value = parseFloat(value.toString()).toFixed(this.props.decimals);

    this.setState({ caret, value }, changeCallback);
  }

  valueIsScoreValue(val: ClinicalNumericValue): val is LinkedNumericValue {
    return !!val && typeof val === 'object' && val.hasOwnProperty('value');
  }

  fieldIsLinkedToScore() {
    return !!this.props.scoreGoalLink && !!this.props.scoreGoalLink.id;
  }

  getNumericValue(val: ClinicalNumericValue) {
    if (this.valueIsScoreValue(val)) {
      return val.value;
    }
    return val;
  }

  getLinkedScoreId(): number | null {
    if (this.valueIsScoreValue(this.state.value)) {
      return this.state.value.scoreEntryId;
    }
    return null;
  }

  @bind
  show(e: any) {
    return this.setState({ open: true, anchorEl: e.currentTarget });
  }

  @bind
  hide(reason?: any) {
    const { onSelectNext, onUpdate } = this.props;
    this.setState({ open: false, anchorEl: null });

    onUpdate(this.getNumericValue(this.state.value));

    if (!reason) {
      return onSelectNext();
    }
  }

  @bind
  onTabKeyDown(e: KeyboardEvent) {
    if (e.keyCode === 9) {
      return this.hide();
    }
  }

  @bind
  onKeyDown(e: React.KeyboardEvent<HTMLSpanElement>, v?: any) {
    if (e.keyCode === 13) {
      this.hide();
    }
    return this._refs.numpad.current != null
      ? this._refs.numpad.current.onKeyDown(e, v)
      : undefined;
  }

  @bind
  onKeyPress(e: React.KeyboardEvent<HTMLSpanElement>, v?: any) {
    return this._refs.numpad.current != null
      ? this._refs.numpad.current.onKeyPress(e, v)
      : undefined;
  }

  @bind
  onUnitChange(v: string) {
    this.setState({ unit: v });
    return this.props.onUnitChange(v);
  }

  makeValue(val: number | string) {
    if (this.fieldIsLinkedToScore() && this.valueIsScoreValue(this.state.value)) {
      return { value: val, scoreEntryId: this.getLinkedScoreId() };
    } else {
      return val;
    }
  }

  @bind
  onChange(v: string) {
    let tempValue;
    let dp = this.props.decimals;
    if (v.indexOf('.') !== v.length - 1) {
      tempValue = NumericMixin.limit(v, this.limitedMin, this.limitedMax);
    }
    if (!(tempValue === this.props.min || tempValue === this.props._max)) {
      v = tempValue;
    }

    let val = NumericMixin.truncToDecimals(v, dp);

    const newValue = this.makeValue(val);
    this.setState({ value: newValue });

    let intVal = Math.floor(val);

    let { min, max } = this.props;
    if (!(intVal < (min || -Infinity) || intVal > (max || Infinity))) {
      this.props.onChange(newValue);
    }
  }

  @bind
  onClick(e: React.MouseEvent<HTMLSpanElement>) {
    return this.show(e);
  }

  render() {
    let label, value;
    if (this.props.readOnly) {
      if (!this.props.inline) {
        label = null;
      } else {
        label = (
          <span style={{ color: colours.lightBlue, fontSize: 16, display: 'block' }}>
            {this.props.label}
          </span>
        );
      }
      value = `${this.getNumericValue(this.state.value)} ${
        this.props.unit || this.props.valueUnit || ''
      }`;
      return <ReadOnly label={label} value={value} />;
    }

    const hasUnits = (this.props.src || []).length > 0;
    const isUnitsArray = Array.isArray(this.props.src);
    const numPadClassName = `bz-column bz-col-${isUnitsArray ? '70' : '100'}`;
    let units: JSX.Element | undefined;
    if (hasUnits) {
      label = <span className="bz-numeric-label">{this.props.src}</span>;

      if (isUnitsArray) {
        units = (
          <div className="bz-column bz-col-30">
            <div className="bz-scroll">
              <ButtonGroup
                src={this.props.src}
                mode="single"
                display="list"
                value={this.state.unit}
                onChange={this.onUnitChange}
              />
            </div>
          </div>
        );

        label = <span className="bz-numeric-label">{this.props.unit || this.props.valueUnit}</span>;
      }
    }

    const style: React.CSSProperties = {}; // {background:'#DDEAF6', border:'1px solid #B6D3EC'}
    const labelStyle = {}; // {color:'#1E4E79', fontWeight:'bold'}

    if (this.props.errored) {
      style.background = colours.red200;
      style.background = colours.red400;
      style.color = `${colours.red} !important`;
    } else if (this.props.warning && this.props.value == null) {
      style.background = colours.orange100;
      style.borderColor = colours.orange300;
      style.color = `${colours.orange300} !important`;
    }

    let labelDescription: JSX.Element | undefined;
    if (!this.props.inline && !this.props.hideLabel) {
      labelDescription = (
        <span
          style={{ color: colours.lightBlue, fontSize: 16, display: 'block' }}
          onClick={this.onClick}
        >
          {this.props.label}
        </span>
      );
    }
    return (
      <span className="inline-block">
        {labelDescription}
        <Popover
          open={this.state.open}
          anchorEl={this.state.anchorEl}
          className=" bz-numeric"
          onRequestClose={this.hide}
        >
          <QuickPickContainer onRequestClose={this.hide} closeButton={true}>
            <div className="bz-row">
              <div className={numPadClassName}>
                <NumPad
                  ref={this._refs.numpad}
                  value={this.getNumericValue(this.state.value)}
                  onChange={this.onChange}
                  decimals={this.props.decimals}
                  colSpan={3}
                  min={this.props.min}
                  max={this.props.max}
                  close={this.hide}
                />
              </div>
              {units}
            </div>
          </QuickPickContainer>
        </Popover>
        <span onKeyPress={this.onKeyPress} onKeyDown={this.onKeyDown} onClick={this.show}>
          <OutlineButton
            {...this.props}
            style={style}
            labelStyle={labelStyle}
            onFocus={this.show}
            onKeyDown={this.onTabKeyDown}
            disabled={this.props.disabled}
          >
            {this.getNumericValue(this.state.value) || this.props.placeholder || <span>–</span>}
            {label}
          </OutlineButton>
        </span>
      </span>
    );
  }
}

export default observer(ClinicalBaseControl(Numeric));
